import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-primary flex items-center justify-center px-6 relative">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <Link to="/" className="font-serif text-2xl" data-testid="brand-logo">CakesbyaKid</Link>
        <h1 className="font-serif text-4xl sm:text-5xl mt-8 leading-tight">Bakery<br />staff login.</h1>
        <p className="text-secondary mt-4">Access the order book and manage incoming cake requests.</p>

        <form onSubmit={submit} className="mt-10 space-y-6 bg-white border border-border rounded-sm p-8" data-testid="login-form">
          <Button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-signin-button"
            className="w-full bg-white border border-border text-primary hover:bg-muted rounded-sm h-12 text-base flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative flex items-center gap-3">
            <span className="flex-1 h-px bg-border" />
            <span className="text-[10px] uppercase tracking-widest text-secondary">or with password</span>
            <span className="flex-1 h-px bg-border" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-secondary">Email</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cakemaster@cakesbyakid.com" className="h-12 bg-white border-border rounded-sm focus:ring-1 focus:ring-accent" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-secondary">Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 bg-white border-border rounded-sm focus:ring-1 focus:ring-accent" />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit-button" className="btn-sweep w-full bg-primary text-primary-foreground hover:text-primary-foreground rounded-sm h-12 text-base">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <Link to="/" className="text-sm text-secondary hover:text-primary transition-colors mt-6 inline-block" data-testid="back-home">← Back to website</Link>
      </motion.div>
    </div>
  );
}
